import * as React from "react";
import { useEffect, useState } from "react";
import styled, { keyframes, css } from "styled-components";
import GameRule from "./Types/GameRule";
import CardRule from "./Types/CardRule";
import Direction from "./Types/Direction";
import { getCardRule } from "./utils";
import { DragElementWrapper, DragPreviewOptions } from "react-dnd";

const getCardTooltip = (number: number, gameRule: GameRule, gameId: number) => {
  const rule = getCardRule(number, gameRule, gameId);
  let tooltip = "";

  switch (rule) {
    case CardRule.Bomb:
      tooltip =
        "This card means that either you or the next person after you must cover it, or the game session is lost.";
      break;
    case CardRule.NoMagic:
      tooltip =
        "As long as this card is showing in any of the 4 stacks, no player is allowed to use magic in any of the 4 stacks.";
      break;
    case CardRule.Stop:
      tooltip =
        // eslint-disable-next-line max-len
        "This card means your turn is over when it's played. You are not allowed to play any cards after a stop card, even if you played it as your first card.";
      break;
    case CardRule.SinglePile:
      tooltip =
        // eslint-disable-next-line max-len
        "As long as this card is showing in any of the 4 stacks, the current player must play all their cards the same stack (of their choice).";
      break;
    case CardRule.MustPlaceThreeCards:
      tooltip =
        // eslint-disable-next-line max-len
        "This card means that when you play it, you must place three and exactly three cards in that turn. When combined with a stop card, the stop card must go last.";
      break;
    case CardRule.Skull:
      tooltip =
        // eslint-disable-next-line max-len
        "You (the player) must cover this card within the same turn (meaning you cannot leave this card showing when your turn is over).";
      break;
    case CardRule.NoSpeech:
      tooltip =
        // eslint-disable-next-line max-len
        "As long as this card is showing in any of the 4 stacks, the players are not allowed to communicate (in any form) with each other.";
      break;
    case CardRule.DrawOnlyOneCard:
      tooltip =
        // eslint-disable-next-line max-len
        "As long as this card is showing in any of the 4 stacks, each player will draw only one card at the end of their turn (meaning you will end up with fewer cards.";
      break;
    default:
      break;
  }

  return tooltip;
};

const pulse = keyframes`
	0% {
		transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.7);
	}

	50% {
		transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(0, 0, 0, 0);

	}

	100% {
		transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);

  }
`;

const pulse2 = keyframes`
	0% {
    background-color: beige;
    color: white;
	}

	50% {
    background-color: #b5b58f;
    color: white;
	}
`;

export interface Props {
  enabled?: boolean;
  selected?: boolean;
  hideThickBorder?: boolean;
  isHighlighted?: boolean;
  fontSize?: number;
  direction?: Direction;
  isFacingDown?: boolean;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  className?: string;
  number?: number;
  gameRule?: GameRule;
  isAnyCardSelected?: boolean;
  ref?: DragElementWrapper<DragPreviewOptions>;
  gameId: number;
}

const StyledCard = styled.div<Props & { cardRule?: CardRule }>`
  height: 3em;
  width: 2em;

  ${({ isHighlighted }) =>
    isHighlighted &&
    css`
      animation: ${pulse} 0.8s linear forwards;
      animation-iteration-count: 3;
    `}
`;

const CardInner = styled.div<Props & { cardRule?: CardRule }>`
  background-color: beige;
  /* opacity: ${({ enabled }) => !enabled && `0.2`};  */

  background-image: ${({ cardRule }) => {
    if (cardRule) {
      switch (cardRule) {
        case CardRule.Bomb:
          return `url("${process.env.PUBLIC_URL}/bomb.svg");`;
        case CardRule.Stop:
          return `url("${process.env.PUBLIC_URL}/stop.svg");`;
        case CardRule.Skull:
          return `url("${process.env.PUBLIC_URL}/skull.svg");`;
        case CardRule.MustPlaceThreeCards:
          return `url("${process.env.PUBLIC_URL}/three.svg");`;
        case CardRule.NoMagic:
          return `url("${process.env.PUBLIC_URL}/magic.svg");`;
        case CardRule.SinglePile:
          return `url("${process.env.PUBLIC_URL}/single_pile.svg");`;
        case CardRule.NoSpeech:
          return `url("${process.env.PUBLIC_URL}/no_speaking.svg");`;
        case CardRule.DrawOnlyOneCard:
          return `url("${process.env.PUBLIC_URL}/draw_only_one_card.svg");`;
      }
    }
  }};
  background-color: ${({ cardRule }) => {
    if (cardRule) {
      switch (cardRule) {
        case CardRule.NoMagic:
        case CardRule.DrawOnlyOneCard:
        case CardRule.NoSpeech:
        case CardRule.SinglePile:
          return `lightblue`;
        case CardRule.Bomb:
          return `#FFA664`;
        case CardRule.Stop:
        case CardRule.MustPlaceThreeCards:
        case CardRule.Skull:
          return `#FFC2B5`;
        default:
          return "beige";
      }
    }
  }};

  background-repeat: no-repeat;
  background-position: center 2em;
  background-size: 40%;
  color: black;
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  flex-direction: column;

  ${({ isHighlighted }) =>
    isHighlighted &&
    css`
      animation: ${pulse2} 0.8s linear forwards;
      animation-iteration-count: 3;
    `}

  ${({ selected }) => selected && `background-color: #b5b58f; color: white;`}
  ${({ fontSize }) => fontSize && `font-size: ${fontSize}rem`}

  ${({ enabled, isAnyCardSelected }) =>
    enabled &&
    !isAnyCardSelected &&
    `:hover {
    background-color: #b5b58f;
    color: white;
  }
    `}

  ${({ isFacingDown }) =>
    isFacingDown &&
    `background: linear-gradient(
        to bottom right,
        #80805c, #b5b58f
      ); 
    `}
    
  box-shadow: 0 0.1em 0.4em rgba(255, 255, 255, 0.28);
  border-radius: 0.15em;
  font-weight: 700;
  position: relative;

  &:after {
    content: "";
    position: absolute;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: ${({ direction }) => {
      return direction === Direction.Up
        ? `0 3rem 3rem 3rem;`
        : `3rem 3rem 0 3rem;`;
    }};

    border-color: ${({ direction }) => {
      return direction === Direction.Up
        ? `transparent transparent #26242e transparent`
        : `#26242e transparent transparent transparent`;
    }};

    ${({ direction }) => {
      return direction === Direction.Up ? `top: 2rem;` : `bottom: 2rem;`;
    }};

    ${({ isFacingDown }) => {
      return isFacingDown && `display: none;`;
    }};

    ${({ direction }) => {
      return !direction ? `opacity: 0;` : `opacity: 0.2`;
    }};
  }

  &:before {
    content: "";
    position: absolute;
    z-index: -1;
    left: 0;
    right: 0;
    bottom: -0.1em;
    height: 0;
    border-bottom: ${({ hideThickBorder }) =>
        hideThickBorder ? `0px` : `0.7em`}
      solid ${({ isFacingDown }) => (!isFacingDown ? `#80805c` : `#000`)};
    border-radius: 0.15em;
  }
`;

const CardUpside = styled.div`
  font-size: 1.5rem;
  opacity: 0.5;
  max-width: 100%;
  overflow: hidden;
  border: 0px !important;
  height: 100%;

  span:nth-child(1) {
    color: white;
    transform: rotate(45deg);
    display: block;
    top: 3.5em;
    position: relative;
  }
  span:nth-child(2) {
    color: #525237;
    transform: rotate(90deg);
  }
  span:nth-child(3) {
    color: black;
    transform: rotate(90deg);
  }
`;

const Card: React.FC<Props> = ({ onClick, ...props }) => {
  const [cardRule, setCardRule] = useState<CardRule>(CardRule.None);
  useEffect(() => {
    if (props.number && props.gameRule) {
      setCardRule(getCardRule(props.number, props.gameRule, props.gameId));
    }
  }, [props.gameId, props.gameRule, props.number]);

  return (
    <StyledCard
      {...props}
      onClick={onClick}
      cardRule={cardRule}
      title={
        props.number && props.gameRule
          ? getCardTooltip(props.number, props.gameRule, props.gameId)
          : ""
      }
    >
      <CardInner {...props} cardRule={cardRule}>
        {props.isFacingDown && (
          <CardUpside>
            <span>i'm closer than you </span>
          </CardUpside>
        )}
        {props.children}
      </CardInner>
    </StyledCard>
  );
};
export default Card;
